import { isAxiosError } from 'axios'
import { DOCUMENT_CATEGORIES } from '@/api/services/documents.service'
import type { UserDocument } from '@/api/services/documents.service'
import { getErrorMessage } from '@/lib/getErrorMessage'

/** Accept attribute for file input (MIME types). */
export const DOCUMENT_UPLOAD_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp,.avif'

/** Max file size in bytes (20 MB — matches backend). */
export const DOCUMENT_UPLOAD_MAX_FILE_SIZE = 20 * 1024 * 1024

/** Max number of files per upload batch — matches backend UPLOAD_MAX_FILES. */
export const DOCUMENT_UPLOAD_MAX_FILES = 10

/** Allowed file extensions for validation. */
export const DOCUMENT_UPLOAD_ACCEPT_EXT = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.csv',
  '.txt',
  '.rtf',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.avif',
]

export interface DocumentCategoryOption {
  value: string
  label: string
}

export interface DocumentGroup {
  id: string
  documentGroupId: string | null
  category: string
  title: string
  fileCount: number
  isGrouped: boolean
  isArchived: boolean
  archivedFileCount: number
  totalSize: number
  latestCreatedAt: string | null
  /** Expiry date shared by the group (null when none set). */
  expiryDate: string | null
  documents: UserDocument[]
  representativeDocument: UserDocument
}

function formatSizeForLimit(bytes: number): string {
  if (bytes >= 1024 * 1024 && bytes % (1024 * 1024) === 0) {
    return `${bytes / (1024 * 1024)} MB`
  }
  if (bytes >= 1024 && bytes % 1024 === 0) {
    return `${bytes / 1024} KB`
  }
  return formatFileSize(bytes)
}

function categoryToLabel(category: string): string {
  return category
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

const CATEGORY_LABEL_OVERRIDES: Record<string, string> = {
  cv: 'CV / Resume',
  'offer-letter': 'Offer Letter',
  cnic: 'CNIC / ID',
  policies: 'Policies',
  contracts: 'Contracts',
  'onboarding-documents': 'Onboarding',
  'health-documents': 'Health',
  'exit-documents': 'Exit',
  'interview-form': 'Interview Form',
  'candidate-documents': 'Candidate Documents',
}

export function formatDocumentCategory(category: string): string {
  return CATEGORY_LABEL_OVERRIDES[category] ?? categoryToLabel(category)
}

export function getDocumentCategoryOptions(
  includeAll = false,
  ownerType?: 'staff' | 'company',
): DocumentCategoryOption[] {
  const companyCategories = new Set(['policies', 'contracts'])
  const candidateCategories = new Set(['interview-form', 'candidate-documents'])
  const filtered = DOCUMENT_CATEGORIES.filter((c) => {
    if (ownerType === 'company') return companyCategories.has(c)
    if (ownerType === 'staff') return !companyCategories.has(c) && !candidateCategories.has(c)
    return true
  })
  const options: DocumentCategoryOption[] = filtered.map((c) => ({
    value: c,
    label: formatDocumentCategory(c),
  }))
  if (includeAll) {
    return [{ value: '', label: 'All categories' }, ...options]
  }
  return options
}

/** Validate files for count, size, and type. Returns error message or null. */
export function validateDocumentFiles(files: File[]): string | null {
  if (files.length > DOCUMENT_UPLOAD_MAX_FILES) {
    return `You can upload at most ${DOCUMENT_UPLOAD_MAX_FILES} files at once`
  }
  for (const file of files) {
    if (file.size > DOCUMENT_UPLOAD_MAX_FILE_SIZE) {
      return `"${file.name}" exceeds the maximum allowed size of ${getDocumentUploadMaxFileSizeLabel()}`
    }
    const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase()
    if (!DOCUMENT_UPLOAD_ACCEPT_EXT.includes(ext)) {
      return `"${file.name}" has an unsupported type. Use PDF, Office, CSV, TXT, RTF, or image files.`
    }
  }
  return null
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function getDocumentUploadMaxFileSizeLabel(): string {
  return formatSizeForLimit(DOCUMENT_UPLOAD_MAX_FILE_SIZE)
}

export function getDocumentUploadHelpText(): string {
  return `Accepted: PDF, Office, CSV, TXT, RTF, or image files, up to ${getDocumentUploadMaxFileSizeLabel()} each. Max ${DOCUMENT_UPLOAD_MAX_FILES} files per upload.`
}

export function getDocumentUploadErrorMessage(error: unknown): string {
  const message = getErrorMessage(error)
  const responseStatus = (error as { response?: { status?: number } })?.response?.status

  if (responseStatus === 413) {
    return `Upload failed. Maximum allowed file size is ${getDocumentUploadMaxFileSizeLabel()} per file.`
  }

  if (isAxiosError(error) && (!error.response || /network error/i.test(message))) {
    return 'Upload failed because the server could not be reached. Check the connection and try again.'
  }

  if (/failed to fetch/i.test(message)) {
    return 'Upload failed because the server could not be reached. Check the connection and try again.'
  }

  return message
}

/** Group documents by documentGroupId. Documents without a groupId each form a single-item group keyed by their id. */
export function groupDocuments(docs: UserDocument[]): Map<string, UserDocument[]> {
  const groups = new Map<string, UserDocument[]>()
  for (const doc of docs) {
    const key = doc.documentGroupId ?? doc.id
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(doc)
  }
  return groups
}

function getDocumentTimestampValue(doc: UserDocument): number {
  if (!doc.createdAt) return 0
  const value = Date.parse(doc.createdAt)
  return Number.isNaN(value) ? 0 : value
}

function sortDocumentsInGroup(documents: UserDocument[]): UserDocument[] {
  return [...documents].sort((left, right) => {
    const createdDiff = getDocumentTimestampValue(right) - getDocumentTimestampValue(left)
    if (createdDiff !== 0) return createdDiff
    return left.originalName.localeCompare(right.originalName)
  })
}

export function buildDocumentGroups(docs: UserDocument[]): DocumentGroup[] {
  const grouped = groupDocuments(docs)
  return Array.from(grouped.entries()).map(([groupId, documents]) => {
    const sortedDocuments = sortDocumentsInGroup(documents)
    const representativeDocument = sortedDocuments[0]
    const latestCreatedAt = representativeDocument.createdAt ?? null
    const archivedFileCount = sortedDocuments.filter((doc) => !!doc.archivedAt).length

    return {
      id: groupId,
      documentGroupId: representativeDocument.documentGroupId,
      category: representativeDocument.category,
      title: representativeDocument.originalName,
      fileCount: sortedDocuments.length,
      isGrouped: sortedDocuments.length > 1,
      isArchived: archivedFileCount === sortedDocuments.length,
      archivedFileCount,
      totalSize: sortedDocuments.reduce((total, doc) => total + doc.size, 0),
      latestCreatedAt,
      expiryDate:
        sortedDocuments.find((doc) => doc.expiryDate)?.expiryDate ?? null,
      documents: sortedDocuments,
      representativeDocument,
    }
  })
}

export function getDistinctDocumentCount(docs: UserDocument[]): number {
  return buildDocumentGroups(docs).length
}
